import React from 'react';
import { motion } from 'framer-motion';
import PageTemplate from '../../components/Common/PageTemplate';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { trackExternalClick } from '../../utils/analytics';
import {
    Globe,
    ExternalLink,
    GraduationCap,
    FileText,
    Mail,
    Phone,
    MapPin,
    BookOpen,
    Users,
    ClipboardList,
    Award,
    Building,
    Info,
    CheckCircle,
    AlertCircle,
    ChevronRight,
    Link2,
    DollarSign,
    Clock,
    FileCheck,
    MousePointer
} from 'lucide-react';

const UluslararasiOgrenciler = () => {
    const location = useLocation();
    const { i18n } = useTranslation();
    const currentPathLang = location.pathname.startsWith('/en') ? 'en' : 'tr';
    const t = i18n.getFixedT(currentPathLang);

    // Fen Fakültesi bölümleri
    const departments = [
        {
            name: t('international_students.departments.actuarial'),
            icon: Award,
            color: 'bg-purple-100 text-purple-700'
        },
        {
            name: t('international_students.departments.biology'),
            icon: Users,
            color: 'bg-green-100 text-green-700'
        },
        {
            name: t('international_students.departments.chemistry'),
            icon: Building,
            color: 'bg-orange-100 text-orange-700'
        },
        {
            name: t('international_students.departments.mathematics'),
            icon: BookOpen,
            color: 'bg-blue-100 text-blue-700'
        },
        {
            name: t('international_students.departments.statistics'),
            icon: FileText,
            color: 'bg-teal-100 text-teal-700'
        }
    ];

    // Gerekli belgeler
    const requiredDocuments = t('international_students.required_documents.items', { returnObjects: true }) || [
        'Lise diploması veya geçici mezuniyet belgesi',
        'Transkript (not dökümü)',
        'TR-YÖS veya kabul edilen sınav sonuç belgesi',
        'Pasaport fotokopisi',
        'Vesikalık fotoğraf',
        'Denklik belgesi (gerekirse)'
    ];

    // 2025-2026 Akademik Yılı Başvuru Linkleri
    const admissionLinks = [
        {
            key: 'step_by_step_guide',
            title: t('international_students.admission_2025.links.step_by_step_guide'),
            icon: ClipboardList,
            url: 'https://internationalstudent.hacettepe.edu.tr/en/menu/stepbystep_application_guide-45',
            color: 'bg-blue-500'
        },
        {
            key: 'requirements',
            title: t('international_students.admission_2025.links.requirements'),
            icon: FileCheck,
            url: 'https://internationalstudent.hacettepe.edu.tr/en/menu/requirements_for_application-47',
            color: 'bg-indigo-500'
        },
        {
            key: 'required_docs',
            title: t('international_students.admission_2025.links.required_docs'),
            icon: FileText,
            url: 'https://internationalstudent.hacettepe.edu.tr/en/menu/required_documents-49',
            color: 'bg-purple-500'
        },
        {
            key: 'accepted_scores',
            title: t('international_students.admission_2025.links.accepted_scores'),
            icon: Award,
            url: 'https://fs.hacettepe.edu.tr/intstudent/dosyalar/mevzuat/004_Accepted_Scores_2025_.pdf',
            color: 'bg-green-500'
        },
        {
            key: 'quotas',
            title: t('international_students.admission_2025.links.quotas'),
            icon: Users,
            url: 'https://internationalstudent.hacettepe.edu.tr/en/menu/student_quotas-53',
            color: 'bg-teal-500'
        },
        {
            key: 'medium_of_instruction',
            title: t('international_students.admission_2025.links.medium_of_instruction'),
            icon: BookOpen,
            url: 'https://internationalstudent.hacettepe.edu.tr/en/menu/medium_of_instruction-55',
            color: 'bg-cyan-500'
        },
        {
            key: 'tuition_fees',
            title: t('international_students.admission_2025.links.tuition_fees'),
            icon: DollarSign,
            url: 'https://fs.hacettepe.edu.tr/intstudent/007_2025-2026%20Tuition_Fees.pdf',
            color: 'bg-orange-500'
        },
        {
            key: 'deadlines',
            title: t('international_students.admission_2025.links.deadlines'),
            icon: Clock,
            url: 'https://internationalstudent.hacettepe.edu.tr/en/menu/application_deadlines-59',
            color: 'bg-red-500'
        },
        {
            key: 'directive',
            title: t('international_students.admission_2025.links.directive'),
            icon: FileCheck,
            url: 'https://fs.hacettepe.edu.tr/oidb/dosyalar/mevzuat/EN_Duzeltme_Secme%20Komisyonu%20Karar%20Verir_Yurtdisindan_Ogrenci_Kabulu_Yonerge_2025_Senatodan_Gecen%20(1).pdf',
            color: 'bg-gray-600'
        },
        {
            key: 'online_application',
            title: t('international_students.admission_2025.links.online_application'),
            icon: MousePointer,
            url: 'https://bilsis.hacettepe.edu.tr/oibs/foa_app/',
            color: 'bg-emerald-500'
        }
    ];

    const cardVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    return (
        <PageTemplate
            title={t('international_students.title')}
            subtitle={t('international_students.subtitle')}
            backgroundImage="/fenfakultesivideo.mp4"
            isVideo={true}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                {/* Hoş Geldiniz Kartı */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl shadow-xl p-8 mb-12 text-white"
                >
                    <div className="flex items-start gap-4">
                        <div className="p-3 bg-white/20 rounded-xl">
                            <Globe className="w-8 h-8" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold mb-3">{t('international_students.welcome.title')}</h2>
                            <p className="text-blue-100 leading-relaxed">
                                {t('international_students.welcome.description')}
                            </p>
                        </div>
                    </div>
                </motion.div>

                {/* Kampüs Görselleri */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.05 }}
                    className="mb-12"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="relative group overflow-hidden rounded-2xl shadow-lg">
                            <img
                                src="https://universitem.hacettepe.edu.tr/wp-content/uploads/2021/04/hakkinda09.jpg"
                                alt="Hacettepe University Campus"
                                className="w-full h-64 md:h-80 object-cover transform group-hover:scale-105 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-6">
                                <h3 className="text-white font-semibold text-lg drop-shadow-lg">
                                    {t('international_students.campus_images.beytepe')}
                                </h3>
                            </div>
                        </div>
                        <div className="relative group overflow-hidden rounded-2xl shadow-lg">
                            <img
                                src="https://universitem.hacettepe.edu.tr/wp-content/uploads/2021/04/hakkinda08.jpg"
                                alt="Hacettepe University Campus Life"
                                className="w-full h-64 md:h-80 object-cover transform group-hover:scale-105 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-6">
                                <h3 className="text-white font-semibold text-lg drop-shadow-lg">
                                    {t('international_students.campus_images.campus_life')}
                                </h3>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Fen Fakültesi Bölümleri */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="mb-12"
                >
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                        <GraduationCap className="w-6 h-6 text-blue-600" />
                        {t('international_students.departments.title')}
                    </h2>
                    <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
                        <p className="text-gray-600 mb-6">{t('international_students.departments.description')}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                            {departments.map((dept, index) => (
                                <motion.div
                                    key={index}
                                    variants={cardVariants}
                                    initial="hidden"
                                    animate="visible"
                                    transition={{ delay: 0.05 * index }}
                                    className={`${dept.color} rounded-lg p-4 text-center`}
                                >
                                    <dept.icon className="w-8 h-8 mx-auto mb-2" />
                                    <span className="font-medium text-sm">{dept.name}</span>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </motion.section>

                {/* 2025-2026 Akademik Yılı Başvuru Bilgileri */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="mb-12"
                >
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <Link2 className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white">
                                        {t('international_students.admission_2025.title')}
                                    </h2>
                                    <p className="text-blue-100 text-sm mt-1">
                                        {t('international_students.admission_2025.description')}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Links Grid */}
                        <div className="p-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                                {admissionLinks.map((link, index) => (
                                    <motion.a
                                        key={link.key}
                                        href={link.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variants={cardVariants}
                                        initial="hidden"
                                        animate="visible"
                                        transition={{ delay: 0.05 * index }}
                                        className="group flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-all duration-200 border border-gray-200 hover:border-gray-300 hover:shadow-md"
                                    >
                                        <div className={`p-2 ${link.color} rounded-lg flex-shrink-0`}>
                                            <link.icon className="w-4 h-4 text-white" />
                                        </div>
                                        <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 flex-1">
                                            {link.title}
                                        </span>
                                        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-transform" />
                                    </motion.a>
                                ))}
                            </div>
                        </div>

                        {/* Online Application Notice */}
                        <div className="px-6 pb-6">
                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                                <div className="flex items-start gap-3">
                                    <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                    <p className="text-blue-800 text-sm">
                                        {t('international_students.admission_2025.system_notice')}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Important Notes */}
                        <div className="bg-gray-50 border-t border-gray-100 p-6">
                            <div className="flex items-start gap-3 mb-4">
                                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                <h3 className="font-bold text-red-700">{t('international_students.admission_2025.important_title')}</h3>
                            </div>
                            <ul className="space-y-3">
                                <li className="flex items-start gap-2 text-sm text-gray-700">
                                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                    <span>
                                        {t('international_students.admission_2025.notes.tests_diplomas')}{' '}
                                        <a
                                            href="https://fs.hacettepe.edu.tr/intstudent/dosyalar/mevzuat/004_Accepted_Scores_2025_.pdf"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:underline font-medium"
                                        >
                                            {t('international_students.admission_2025.notes.click_here')}
                                        </a>
                                    </span>
                                </li>
                                <li className="flex items-start gap-2 text-sm text-gray-700">
                                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                    <span>
                                        {t('international_students.admission_2025.notes.tr_yos_note')}{' '}
                                        <a
                                            href="https://www.osym.gov.tr"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:underline font-medium"
                                        >
                                            www.osym.gov.tr
                                        </a>
                                    </span>
                                </li>
                                <li className="flex items-start gap-2 text-sm text-gray-700">
                                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                    <span>{t('international_students.admission_2025.notes.no_partnership')}</span>
                                </li>
                                <li className="flex items-start gap-2 text-sm text-gray-700">
                                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                    <span>{t('international_students.admission_2025.notes.personal_application')}</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </motion.section>

                {/* Öğrenci Kılavuzu */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="mb-12"
                >
                    <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 rounded-2xl shadow-xl">
                        {/* Background Pattern */}
                        <div className="absolute inset-0 opacity-10">
                            <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
                            <div className="absolute bottom-0 right-0 w-60 h-60 bg-white rounded-full translate-x-1/3 translate-y-1/3" />
                            <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
                        </div>

                        <div className="relative p-8 md:p-10">
                            <div className="flex flex-col md:flex-row items-center gap-8">
                                {/* Icon */}
                                <div className="flex-shrink-0">
                                    <div className="w-24 h-24 md:w-32 md:h-32 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg">
                                        <BookOpen className="w-12 h-12 md:w-16 md:h-16 text-white" />
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="flex-1 text-center md:text-left">
                                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                                        {t('international_students.student_handbook.title')}
                                    </h2>
                                    <p className="text-white/90 text-lg mb-6 max-w-2xl">
                                        {t('international_students.student_handbook.description')}
                                    </p>
                                    <a
                                        href="https://fs.hacettepe.edu.tr/fenyeni/dosyalar/ogrencikilavuzu/ogrencikilavuzu.pdf"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-3 bg-white text-indigo-600 px-8 py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 hover:bg-gray-50"
                                    >
                                        <FileText className="w-5 h-5" />
                                        {t('international_students.student_handbook.button')}
                                        <ExternalLink className="w-5 h-5" />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.section>

                {/* Gerekli Belgeler */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="mb-16"
                >
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                        <FileText className="w-6 h-6 text-blue-600" />
                        {t('international_students.required_documents.title')}
                    </h2>
                    <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                        {/* Format Notice */}
                        <div className="bg-blue-50 border-b border-blue-100 p-4">
                            <div className="flex items-center gap-3">
                                <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
                                <p className="text-blue-800 text-sm font-medium">
                                    {t('international_students.required_documents.format_notice')}
                                </p>
                            </div>
                        </div>
                        {/* Documents List */}
                        <div className="p-6">
                            <div className="space-y-4">
                                {requiredDocuments.map((doc, index) => (
                                    <motion.div
                                        key={index}
                                        variants={cardVariants}
                                        initial="hidden"
                                        animate="visible"
                                        transition={{ delay: 0.05 * index }}
                                        className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100"
                                    >
                                        <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                            <CheckCircle className="w-5 h-5 text-green-600" />
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="font-semibold text-gray-800 mb-1">
                                                {typeof doc === 'object' ? doc.title : doc}
                                            </h4>
                                            {typeof doc === 'object' && doc.description && (
                                                <p className="text-gray-600 text-sm leading-relaxed">
                                                    {doc.description}
                                                </p>
                                            )}
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>
                </motion.section>

                {/* İletişim */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                >
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                        <Mail className="w-6 h-6 text-blue-600" />
                        {t('international_students.contact.title')}
                    </h2>
                    <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                        <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
                            {/* Beytepe Ofisi */}
                            <div className="p-6">
                                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                    <Building className="w-5 h-5 text-blue-600" />
                                    {t('international_students.contact.beytepe_office.title')}
                                </h3>
                                <div className="space-y-3">
                                    <div className="flex items-start gap-3 text-gray-600">
                                        <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5 text-gray-400" />
                                        <span className="text-sm">{t('international_students.contact.beytepe_office.address')}</span>
                                    </div>
                                    <div className="flex items-start gap-3 text-gray-600">
                                        <Phone className="w-5 h-5 flex-shrink-0 mt-0.5 text-gray-400" />
                                        <div className="text-sm">
                                            <p>+90 312 297 61 91</p>
                                            <p>+90 312 297 65 70 /170 – 171</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Sıhhiye Ofisi */}
                            <div className="p-6">
                                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                    <Building className="w-5 h-5 text-blue-600" />
                                    {t('international_students.contact.sihhiye_office.title')}
                                </h3>
                                <div className="space-y-3">
                                    <div className="flex items-start gap-3 text-gray-600">
                                        <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5 text-gray-400" />
                                        <span className="text-sm">{t('international_students.contact.sihhiye_office.address')}</span>
                                    </div>
                                    <div className="flex items-start gap-3 text-gray-600">
                                        <Phone className="w-5 h-5 flex-shrink-0 mt-0.5 text-gray-400" />
                                        <span className="text-sm">+90 312 305 21 41</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* E-posta Bölümü */}
                        <div className="border-t border-gray-100 p-6 bg-gray-50">
                            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                <Mail className="w-5 h-5 text-blue-600" />
                                {t('international_students.contact.email.title')}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-white rounded-lg p-4 border border-gray-200">
                                    <p className="text-sm text-gray-600 mb-2">
                                        {t('international_students.contact.email.admission_label')}
                                    </p>
                                    <a
                                        href="mailto:admission@hacettepe.edu.tr"
                                        className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-2"
                                    >
                                        <Mail className="w-4 h-4" />
                                        admission@hacettepe.edu.tr
                                    </a>
                                </div>
                                <div className="bg-white rounded-lg p-4 border border-gray-200">
                                    <p className="text-sm text-gray-600 mb-2">
                                        {t('international_students.contact.email.registered_label')}
                                    </p>
                                    <a
                                        href="mailto:int.stu@hacettepe.edu.tr"
                                        className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-2"
                                    >
                                        <Mail className="w-4 h-4" />
                                        int.stu@hacettepe.edu.tr
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.section>

                {/* Resmi Başvuru Sayfasına Yönlendirme */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.8 }}
                    className="mt-12 text-center"
                >
                    <a
                        href="https://internationalstudent.hacettepe.edu.tr/en"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackExternalClick('https://internationalstudent.hacettepe.edu.tr/en', 'Official Application Page')}
                        className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-8 py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                    >
                        <Globe className="w-5 h-5" />
                        {t('international_students.cta_button')}
                        <ExternalLink className="w-5 h-5" />
                    </a>
                </motion.div>
            </div>
        </PageTemplate>
    );
};

export default UluslararasiOgrenciler;
